import { ref, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { defineStore } from 'pinia'

import api from '@/utils/api'
import { useFileCreator } from '@/utils/dnsRecords.utils'

import { DnsZoneBase, DnsZoneFull, DnsRRSetBase, DnsRecordFull, DnsRecordBase } from '@/models/DNS.model'

export const useDnsStore = defineStore('dnsStore', () => {
  const router = useRouter()
  const instance = getCurrentInstance()
  const { $toast, $modal } = instance.appContext.config.globalProperties
  const { t } = useI18n()

  const zones = ref([])
  const recordSets = ref([])
  const records = ref([])

  const selectedZones = ref([])
  const selectedRecords = ref([])

  const currentZone = ref(null)
  const currentRecord = ref(null)
  const editableZone = ref(null)
  const editableRecord = ref(null)
  const editableRecordId = ref(null)

  const loading = ref(false)

  // zone's methods
  const toggleDnsZoneSelect = (zone) => {
    const itemIndex = selectedZones.value.findIndex(z => z.name === zone.name)

    if (itemIndex >= 0) {
      selectedZones.value.splice(itemIndex, 1)
    } else selectedZones.value.push(zone)
  }
  const toggleAllDnsZonesSelect = () => {
    selectedZones.value = selectedZones.value.length === zones.value.length
      ? []
      : zones.value.map(zone => zone)
  }
  const fetchDnsZones = async () => {
    if (loading.value) return
    loading.value = true

    try {
      const { data } = await api.getZones()
      zones.value = data.map(z => new DnsZoneBase(z))
    } catch {
      $toast.show({
        text: t('При загрузке произошла ошибка')
      })
    } finally {
      loading.value = false
    }
  }
  const fetchDnsZone = async (zoneName) => {
    await router.push({ name: 'dns', params: { name: zoneName } })
    try {
      const { data } = await api.getZone({ name: zoneName })
      currentZone.value = new DnsZoneFull(data)
    } catch {
      $toast.show({
        text: t('При загрузке произошла ошибка')
      })
    }
  }
  const createDnsZone = async (zone) => {
    if (loading.value) return

    try {
      loading.value = true
      const { data } = await api.createZone(zone)

      const newDnsZone = new DnsZoneFull({ ...data })
      zones.value.push(newDnsZone)

      $toast.show({
        text: t('Доменная зона создана')
      })
    } catch {
      $toast.show({
        text: t('При создании доменной зоны произошла ошибка')
      })
    } finally {
      loading.value = false
    }
  }
  const updateDnsZone = async (name, updatePayload) => {
    if (loading.value) return

    try {
      loading.value = true
      const currentZone = zones.value.find(z => z.name === name)

      const { data } = await api.updateZone({
        name,
        ...updatePayload
      })

      const newDnsZone = new DnsZoneFull({ ...data })
      Object.keys(currentZone).forEach(key => {
        if (newDnsZone[key] !== undefined) {
          currentZone[key] = newDnsZone[key]
        }
      })

      $toast.show({
        text: t('Доменная зона обновлена')
      })
    } catch (e) {
      $toast.show({
        text: t('При обновлении доменной зоны произошла ошибка')
      })
    } finally {
      loading.value = false
    }
  }
  const removeDnsZone = async (zoneName) => {
    try {
      await api.removeZone({ name: zoneName })

      const itemIndex = zones.value.findIndex(z => z.name === zoneName)
      const selectedItemIndex = selectedZones.value.findIndex(z => z.name === zoneName)
      zones.value.splice(itemIndex, 1)
      selectedZones.value.splice(selectedItemIndex, 1)
    } catch {
      $toast.show({
        text: t('При удалении произошла ошибка')
      })
    } finally {
      $toast.show({
        text: t('Доменная зона удалена')
      })
    }
  }
  const toggleDnsZoneActivation = async (zoneName, method = 'enabled') => {
    if (loading.value) return

    try {
      loading.value = true
      const currentZone = zones.value.find(z => z.name === zoneName)

      if (method === 'disable') {
        const { data } = await api.disableZone({ name: zoneName })
        $toast.show({
          text: t('Доменная зона деактивирована')
        })
      } else {
        const { data } = await api.enableZone({ name: zoneName })
        $toast.show({
          text: t('Доменная зона активирована')
        })
      }
      currentZone.enabled = !currentZone.enabled
    } catch {
      $toast.show({
        text: selectedZones.value[0].enabled ? t('При деактивации доменной зоны произошла ошибка') : t('При активации доменной зоны произошла ошибка')
      })
    } finally {
      selectedZones.value = []
      loading.value = false
    }
  }

  // resource record sets
  const fetchRRSets = async (zoneName) => {
    if (loading.value) return
    loading.value = true

    try {
      const { data } = await api.getZoneRRSets({ zoneName: zoneName })
      recordSets.value = data.map(r => new DnsRRSetBase(r))
    } catch {
      $toast.show({
        text: t('При загрузке произошла ошибка')
      })
    } finally {
      loading.value = false
    }
  }
  const updateRRset = async (zoneName, rRSetName, rRSetType, payload) => {
    if (loading.value) return
    loading.value = true

    const currentRRSet = recordSets.value.find(s => s.rrType === rRSetType && s.rrName === rRSetName)

    try {
      const { data } = await api.updateZoneRRSetByType({
        zoneName: zoneName,
        rRSetName: rRSetName,
        rRSetType: rRSetType,
        ttl: currentRRSet.ttl,
        records: records.value.filter(r => r.rrType === rRSetType && r.rrName === rRSetName).map(r => ({ content: [r.content], disabled: r.disabled, weight: r.weight })),
        weights_enabled: currentRRSet.weightsEnabled,
        ...payload
      })

      const newRRSet = new DnsRRSetBase({ ...data })
      Object.keys(currentRRSet).forEach(key => {
        if (newRRSet[key] !== undefined) {
          currentRRSet[key] = newRRSet[key]
        }
      })
    } catch {
      $toast.show({
        text: t('При изменении произошла ошибка')
      })
    } finally {
      loading.value = false
    }
  }

  // record's methods
  const toggleDnsRecordSelect = (record) => {
    const itemIndex = selectedRecords.value.findIndex(r => r.id === record.id)

    if (itemIndex >= 0) {
      selectedRecords.value.splice(itemIndex, 1)
    } else selectedRecords.value.push(record)
  }
  const toggleAllDnsRecordsSelect = () => {
    selectedRecords.value = selectedRecords.value.length === records.value.length
      ? []
      : records.value.map(record => record)
  }
  const fetchDnsRecords = async (zoneName) => {
    if (loading.value) return
    loading.value = true
    await router.push({ name: 'dns', params: { name: zoneName } })

    try {
      const { data } = await api.getZoneRecords({ zoneName: zoneName })
      records.value = data.filter(r => r.rrType !== 'SOA').map(r => new DnsRecordFull(r))
    } catch {
      $toast.show({
        text: t('При загрузке произошла ошибка')
      })
    } finally {
      loading.value = false
    }
  }
  const fetchDnsRecord = async (recordId) => {
    try {
      const { data } = await api.getZoneRecord({ id: recordId })
      currentRecord.value = data
    } catch {
      $toast.show({
        text: t('При загрузке произошла ошибка')
      })
    }
  }
  const createDnsRecord = async (record) => {
    if (loading.value) return

    try {
      loading.value = true
      const { data } = await api.createZoneRecord({
        zoneName: currentZone.value.name,
        ...record
      })

      const newRecord = new DnsRecordFull({ ...data })
      records.value.push(newRecord)

      $toast.show({
        text: t('Доменная запись создана')
      })
    } catch {
      $toast.show({
        text: t('При создании доменной записи произошла ошибка')
      })
    } finally {
      loading.value = false
    }

  }
  const updateDnsRecord = async (id, updatePayload) => {
    if (loading.value) return

    try {
      loading.value = true
      const currentRecord = records.value.find(r => r.id === id)

      const { data } = await api.updateZoneRecord({
        zoneName: currentZone.value.name,
        recordId: id,
        ...updatePayload
      })

      const newDnsRecord = new DnsRecordFull({ ...data })
      Object.keys(currentRecord).forEach(key => {
        if (newDnsRecord[key] !== undefined) {
          currentRecord[key] = newDnsRecord[key]
        }
      })

      $toast.show({
        text: t('Доменная запись обновлена')
      })
    } catch (e) {
      $toast.show({
        text: t('При обновлении доменной записи произошла ошибка')
      })
    } finally {
      loading.value = false
      selectedRecords.value = []
    }
  }
  const removeDnsRecord = async (recordId) => {
    try {
      await api.removeZoneRecord({ zoneName: currentZone.value.name, recordId: recordId })

      const itemIndex = records.value.findIndex(r => r.id === recordId)
      const selectedItemIndex = selectedRecords.value.findIndex(r => r.id === recordId)
      records.value.splice(itemIndex, 1)
      selectedRecords.value.splice(selectedItemIndex, 1)
    } catch {
      $toast.show({
        text: t('При удалении произошла ошибка')
      })
    } finally {
      $toast.show({
        text: t('Доменная запись удалена')
      })
    }

  }
  const toggleDnsRecordActivation = async (recordId, method = 'enable') => {
    if (loading.value) return

    try {
      loading.value = true
      const currentRecord = records.value.find(r => r.id === recordId)

      if (method === 'disable') {
        await api.disableZoneRecord({
          zoneName: currentZone.value.name,
          recordId: recordId
        })
        $toast.show({
          text: 'Доменная запись деактивирована'
        })
      } else {
        await api.enableZoneRecord({
          zoneName: currentZone.value.name,
          recordId: recordId
        })
        $toast.show({
          text: 'Доменная запись активирована'
        })
      }
      currentRecord.disabled = !currentRecord.disabled
    } catch {
      $toast.show({
        text: selectedRecords.value[0].enabled ? 'Ошибка при деактивации записи' : 'Ошибка при активации записи'
      })
    } finally {
      selectedRecords.value = []
      loading.value = false
    }
  }
  const importDnsRecords = async () => {
    console.log('import')
  }
  const exportDnsRecords = async () => {
    useFileCreator()
  }

  // modal's methods
  const showDnsZoneModal = () => {
    $modal.show('newDnsZone')
  }
  const showRemoveDnsZonesModal = () => {
    $modal.show('removeDnsZones')
  }
  const showDnsRecordModal = () => {
    $modal.show('newDnsRecord')
  }
  const showRemoveDnsRecordsModal = () => {
    $modal.show('removeDnsRecords')
  }
  const showExportDnsRecordsModal = () => {
    $modal.show('exportDnsRecords')
  }
  const showImportDnsRecordsModal = () => {
    $modal.show('importDnsRecords')
  }
  const hideModal = () => {
    $modal.hide()
  }

  return {
    zones,
    recordSets,
    records,
    selectedZones,
    selectedRecords,
    currentZone,
    currentRecord,
    editableZone,
    editableRecord,
    editableRecordId,
    loading,
    fetchDnsZone,
    fetchDnsZones,
    toggleDnsZoneSelect,
    toggleAllDnsZonesSelect,
    createDnsZone,
    updateDnsZone,
    removeDnsZone,
    toggleDnsZoneActivation,
    toggleDnsRecordSelect,
    toggleAllDnsRecordsSelect,
    fetchRRSets,
    updateRRset,
    fetchDnsRecords,
    fetchDnsRecord,
    createDnsRecord,
    updateDnsRecord,
    removeDnsRecord,
    toggleDnsRecordActivation,
    importDnsRecords,
    exportDnsRecords,
    showDnsZoneModal,
    showRemoveDnsZonesModal,
    showDnsRecordModal,
    showRemoveDnsRecordsModal,
    showExportDnsRecordsModal,
    showImportDnsRecordsModal,
    hideModal
  }
})
